import React, { useState, useRef, useEffect, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { FaUpload, FaDownload, FaFolder, FaPlus, FaSave, FaFolderOpen, FaCode, FaFont } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';
import eventBus from '../../utils/eventBus';
import { saveCode, getCodeFile, showNotification, SaveResult } from '../../services/SaveService';
import { getLanguageTemplate, isSupportedLanguage, CODE_TEMPLATES, Language } from '../../utils/languageUtils'; // 添加 CODE_TEMPLATES 和 Language
import { initSyntaxChecker, SyntaxChecker } from '../../services/SyntaxChecker';
import AiBubble from './AiBubble';
import CodeListModal from './CodeListModal';
import './Editor.css';

declare global {
  interface Window {
    monaco: any; // 声明 monaco 全局变量
  }
}

// 计算字符串的 SHA256 哈希值
async function calculateSHA256(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

const UploadIcon = FaUpload as React.FunctionComponent<IconBaseProps>;
const DownloadIcon = FaDownload as React.FunctionComponent<IconBaseProps>;
const FolderIcon = FaFolder as React.FunctionComponent<IconBaseProps>;
const PlusIcon = FaPlus as React.FunctionComponent<IconBaseProps>;
const SaveIcon = FaSave as React.FunctionComponent<IconBaseProps>;
const FolderOpenIcon = FaFolderOpen as React.FunctionComponent<IconBaseProps>;
const CodeIcon = FaCode as React.FunctionComponent<IconBaseProps>;
const FontIcon = FaFont as React.FunctionComponent<IconBaseProps>;

// 定义组件的 props 接口
interface CodeEditorProps {
  code: string;
  setCode: React.Dispatch<React.SetStateAction<string>>;
  language: string;
  setLanguage: (language: string) => void;
  fileName: string;
  setFileName: React.Dispatch<React.SetStateAction<string>>;
  isCompiling: boolean;
  isRunning: boolean;
}

const EditorWithBus: React.FC<CodeEditorProps> = ({
  code,
  setCode,
  language,
  setLanguage,
  fileName,
  setFileName,
  isCompiling,
  isRunning
}) => {
  // 使用从 props 传入的状态而不是内部状态
  const [font, setFont] = useState<string>('14px');
  const [showFileMenu, setShowFileMenu] = useState<boolean>(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState<boolean>(false);
  const [lastSavedHash, setLastSavedHash] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const fileMenuRef = useRef<HTMLDivElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  const saveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const syntaxCheckerRef = useRef<SyntaxChecker | null>(null);
  
  // AI浮动按钮相关状态
  const [aiBubbleVisible, setAiBubbleVisible] = useState<boolean>(false);
  const [aiBubblePosition, setAiBubblePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [selectedCode, setSelectedCode] = useState<string>('');
  
  // 代码列表弹窗相关状态
  const [showCodeList, setShowCodeList] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredCodeFiles, setFilteredCodeFiles] = useState<any[]>([]);
  
  


  // 自动保存函数 - 使用 useCallback 包装以避免不必要的重渲染
  const autoSaveCode = useCallback(async () => {
    if (!editorRef.current || isSaving) return;
    
    try {
      const content = editorRef.current.getValue();
      if (!content) return;
      
      // 计算当前内容的哈希值
      const currentHash = await calculateSHA256(content);
      
      // 如果内容没有变化，则跳过保存
      if (currentHash === lastSavedHash) {
        console.log('内容未变化，跳过保存');
        return;
      }
      
      setIsSaving(true);
      
      // 获取或生成文档ID
      let docId = localStorage.getItem('current_doc_id');
      if (!docId) {
        docId = 'doc_' + crypto.randomUUID();
        localStorage.setItem('current_doc_id', docId);
      }
      
      // 获取文件名
      const title = fileName || '未命名文件';
      
      // 使用 SaveService 中的 saveCode 函数保存代码
      const result = await saveCode(content, title, language, docId);
      
      if (result.success) {
        console.log('自动保存成功，版本:', result.data?.version);
        setLastSavedHash(currentHash);
        // showNotification 已在 saveCode 函数中调用
      } else {
        console.error('自动保存失败:', result.message);
        // showNotification 已在 saveCode 函数中调用
      }
    } catch (error) {
      console.error('自动保存出错:', error);
      showNotification('保存出错，请检查网络连接', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, language, fileName, lastSavedHash]);

  // 初始化哈希值和设置编辑器变化监听
  useEffect(() => {
    const initializeHash = async () => {
      if (editorRef.current) {
        const content = editorRef.current.getValue() || '';
        if (content) {
          const hash = await calculateSHA256(content);
          setLastSavedHash(hash);
        }
      }
    };
    
    // 初始化时计算一次哈希
    initializeHash();
    
    // 监听代码变化，更新哈希
    const handleEditorChange = (value: string | undefined) => {
      if (value !== undefined) {
        setCode(value);
        // 当代码变化时，清空 lastSavedHash 以便下次保存
        setLastSavedHash('');
      }
    };
    
    // 添加事件监听
    const editor = editorRef.current;
    if (editor) {
      editor.onDidChangeModelContent(() => {
        setLastSavedHash('');
      });
    }
    
    return () => {
      // 清理
      if (editor) {
        editor.dispose();
      }
    };
  }, [setCode]);

  // 设置自动保存定时器
  useEffect(() => {
    // 自动保存函数
    const startAutoSave = () => {
      // 立即执行一次保存
      autoSaveCode();
      // 然后每30秒执行一次
      saveIntervalRef.current = setInterval(autoSaveCode, 30000);
    };

    // 启动自动保存
    startAutoSave();

    // 组件卸载时清除定时器
    return () => {
      if (saveIntervalRef.current) {
        clearInterval(saveIntervalRef.current);
      }
    };
  }, [autoSaveCode]); // 添加 autoSaveCode 到依赖数组

  // 添加全局点击事件监听器，点击其他区域时关闭下拉列表
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // 处理文件菜单
      if (fileMenuRef.current && !fileMenuRef.current.contains(event.target as Node)) {
        setShowFileMenu(false);
      }
      // 处理语言菜单
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setShowLanguageMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFont(e.target.value);
  };

  const handleEditorChange = (value: string | undefined) => {
    if (value !== undefined) {
      setCode(value);
    }
  };

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  const handleLanguageSelect = (lang: string) => {
    // 1. 获取新语言的模板代码和文件名
    // 确保 lang 是 Language 类型
    const template = getLanguageTemplate(lang as Language);
    
    // 2. 更新语言状态
    setLanguage(lang);
    
    // 3. 更新编辑器内容 (通过 setCode，假设 Editor 的 value 绑定到 code)
    setCode(template.code);
    
    // 4. 更新文件名
    setFileName(template.fileName);
    
    setShowLanguageMenu(false);

    const newDocId = 'doc_' + crypto.randomUUID();
    localStorage.setItem('current_doc_id', newDocId); 
    
    // 5. 发送语言变化事件到事件总线
    // 假设 eventBus 对 languageChanged 的定义是 { language: string; code?: string; fileName?: string; }
    // 如果定义更严格，需要相应调整
    eventBus.emit('languageChanged', { language: lang, code: template.code, fileName: template.fileName });
    
    // 6. 延迟禁用验证，确保在编辑器模型更新后执行
    // 使用 setTimeout 确保在DOM更新和Monaco模型更新后执行
    setTimeout(() => {
      const monaco = window.monaco || monacoRef.current;
      if (monaco && editorRef.current) { // 确保 editorRef 也可用
        disableDefaultValidations(monaco);
        
        // 确保光标在新模板的开头
        editorRef.current.setPosition({ lineNumber: 1, column: 1 });
        editorRef.current.focus();
      }
      
      // 7. 更新语法检测器的语言设置并触发检查
      if (syntaxCheckerRef.current) {
        syntaxCheckerRef.current.setLanguage(lang);
        syntaxCheckerRef.current.checkSyntax(); // 检查新模板代码
      }
    }, 0); // setTimeout 0 延迟到下一个事件循环
  };

  const toggleLanguageMenu = () => {
    setShowLanguageMenu(!showLanguageMenu);
  };


  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([code], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${fileName}.${language === 'cpp' ? 'cpp' : 'py'}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };


  const handleSave = () => {
    handleDownload();
  };

  const toggleFileMenu = () => {
    setShowFileMenu(!showFileMenu);
  };

  const handleCreateNewFile = () => {
    // 生成新的文档ID
    const newDocId = 'doc_' + crypto.randomUUID();
    localStorage.setItem('current_doc_id', newDocId); // ✅ 更新当前文档ID
    // 发送语言变化事件到事件总线
    eventBus.emit('languageChanged', { language: language });
    
    if (editorRef.current) {
      let newCode = '// 新文件';
      let newFileName = 'untitled.txt';
      
      // 使用语言工具获取对应的代码模板和文件名
      if (isSupportedLanguage(language)) {
        const { code, fileName } = getLanguageTemplate(language);
        newCode = code;
        newFileName = `untitled${language === 'python' ? '.py' : '.cpp'}`;
      }
      
      // 设置编辑器的值和文件名
      editorRef.current.setValue(newCode);
      setFileName(newFileName);
    }
    
    setShowFileMenu(false);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (editorRef.current) {
          editorRef.current.setValue(content);
          const language = file.name.toLowerCase().endsWith('.py') ? 'python' : 'cpp';
           
          setLanguage(language);
          setFileName(file.name.split('.')[0]);
          const newDocId = 'doc_' + crypto.randomUUID();
          localStorage.setItem('current_doc_id', newDocId); // ✅ 更新当前文档ID
          // 发送语言变化事件到事件总线
          eventBus.emit('languageChanged', { language: language });
          
          // 更新语法检测器的语言设置
          if (syntaxCheckerRef.current) {
            syntaxCheckerRef.current.setLanguage(language);
            // 触发语法检查
            setTimeout(() => {
              syntaxCheckerRef.current?.checkSyntax();
            }, 300); // 延迟执行，确保编辑器内容已更新
          }
        }
        setFileName(file.name);
      };
      reader.readAsText(file);
    }
    setShowFileMenu(false);
  };

  // 处理选择代码文件
  const handleSelectCodeFile = async (fileId: string) => {
    try {
      // 显示加载状态
      setIsLoading(true);
      
      // 使用 SaveService 中的 getCodeFile 函数获取文件内容
      const result = await getCodeFile(fileId);
      
      if (result.success) {
        const fileData = result.data;
        setCode(fileData.content || '');
        setFileName(fileData.title);
        // 根据文件内容或扩展名判断语言
        const language = fileData.language?.toLowerCase() || 'cpp';
        setLanguage(language);

        // 发送语言变化事件到事件总线
        eventBus.emit('languageChanged', { language: language });

        localStorage.setItem('current_doc_id', fileId);
        
        // 更新语法检测器的语言设置
        if (syntaxCheckerRef.current) {
          syntaxCheckerRef.current.setLanguage(language);
          // 触发语法检查
          setTimeout(() => {
            syntaxCheckerRef.current?.checkSyntax();
          }, 300); // 延迟执行，确保编辑器内容已更新
        }
        
        // 显示成功通知
        showNotification(`已加载文件: ${fileData.title}`, 'success');
      } else {
        throw new Error(result.message || '加载文件失败');
      }
      
      
      // 关闭弹窗
      setShowCodeList(false);
    } catch (error) {
      console.error('加载代码文件失败:', error);
      // 使用SaveService中的showNotification函数替代eventBus.emit
      showNotification('加载文件失败: ' + (error instanceof Error ? error.message : '未知错误'), 'error');
    } finally {
      // 无论成功失败，都关闭加载状态
      setIsLoading(false);
    }
  };

  const handleOpenFileClick = () => {
    // 打开代码列表弹窗而不是文件选择器
    setShowCodeList(true);
  };

  const handleSaveFile = async () => {
    console.log(`保存文件: ${fileName}`);
    if (!editorRef.current) {
      console.error('编辑器未初始化');
      return;
    }
    autoSaveCode();

    
  };

  // 处理编辑器挂载 - 合并了两个重复定义的函数
  // 禁用 Monaco Editor 的默认验证
  const disableDefaultValidations = (monacoInstance: any) => {
    if (!monacoInstance) return;
    
    // 为 TypeScript 和 JavaScript 禁用验证
    monacoInstance.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: true
    });
    monacoInstance.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: true,
      noSyntaxValidation: true
    });
    
    // 为 Python 禁用验证
    if (monacoInstance.languages.getLanguages) {
      const pythonLang = monacoInstance.languages.getLanguages().find((l: any) => l.id === 'python');
      if (pythonLang) {
        monacoInstance.languages.register({ id: 'python' });
        monacoInstance.languages.setLanguageConfiguration('python', {});
      }
    }
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
    console.log('编辑器已挂载');
    
    // 禁用默认的语法和语义验证
    disableDefaultValidations(monaco);
    
    // 初始化语法检测器
    syntaxCheckerRef.current = initSyntaxChecker(editor, monaco);
    
    // 设置当前语言
    if (syntaxCheckerRef.current && language) {
      syntaxCheckerRef.current.setLanguage(language);
      // 触发初始语法检查
      syntaxCheckerRef.current.checkSyntax();
    }
    
    // 监听选区变化
    setupSelectionListener(editor, monaco);
    
    // 添加右键菜单事件监听
    editor.onContextMenu((e: any) => {
      const position = e.target.position;
      if (position) {
        const selection = editor.getSelection();
        if (selection && !selection.isEmpty()) {
          const selectedText = editor.getModel().getValueInRange(selection);
          if (selectedText) {
            setSelectedCode(selectedText);
            // 修正 aiBubblePosition 的属性名，使其与定义一致
            setAiBubblePosition({ x: e.event.posx, y: e.event.posy });
            setAiBubbleVisible(true);
          }
        }
      }
    });

    // 添加格式化文档的右键菜单项
    editor.addAction({
      id: 'formatCodeAction',
      label: '格式化文档',
      contextMenuGroupId: 'navigation', // 放在导航组，也可以是 '1_modification'
      run: function(editor: any) {
        editor.getAction('editor.action.formatDocument').run();
      }
    });
  };

  

  
  // 设置选区变化监听
  const setupSelectionListener = (editor: any, monaco: any) => {
    // 监听选区变化
    const disposable = editor.onDidChangeCursorSelection((e: any) => {
      // 获取选区内容
      const selection = e.selection;
      const model = editor.getModel();
      
      // 检查是否有有效选区（非空选区）
      if (selection && 
          !(selection.startLineNumber === selection.endLineNumber && 
            selection.startColumn === selection.endColumn)) {
        
        // 获取选中的文本
        const selectedText = model.getValueInRange(selection);
        
        // 如果选中的文本不为空且长度足够
        if (selectedText && selectedText.trim().length > 10) {
          // 计算选区结束位置在页面中的坐标
          const endPosition = selection.getEndPosition();
          const endCoords = editor.getScrolledVisiblePosition(endPosition);
          
          // 获取编辑器容器的位置信息
          const editorDomNode = editor.getDomNode();
          const editorRect = editorDomNode.getBoundingClientRect();
          
          // 计算浮动按钮的位置（选区结束位置的右下方）
          const bubbleX = editorRect.left + endCoords.left + 10; // 右移 10px
          const bubbleY = editorRect.top + endCoords.top + 20; // 下移 20px
          
          // 更新浮动按钮状态
          setSelectedCode(selectedText);
          setAiBubblePosition({ x: bubbleX, y: bubbleY });
          setAiBubbleVisible(true);
        } else {
          // 选区过小或为空，隐藏浮动按钮
          setAiBubbleVisible(false);
        }
      } else {
        // 无选区，隐藏浮动按钮
        setAiBubbleVisible(false);
      }
    });
    
    // 监听 ESC 键按下事件
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAiBubbleVisible(false);
      }
    };
    
    // 添加键盘事件监听
    window.addEventListener('keydown', handleKeyDown);
    
    // 组件卸载时清理监听器
    return () => {
      disposable.dispose();
      window.removeEventListener('keydown', handleKeyDown);
    };
  };

  // 处理 AI 浮动按钮点击
  const handleAiBubbleClick = () => {
    if (selectedCode && editorRef.current) {
      // 获取完整代码
      const fullCode = editorRef.current.getValue();
      
      // 先触发特殊事件，通知 App 组件显示 AI 助手
      eventBus.emit('showAiAssistant', {});
      
      // 添加小延迟，确保 ChatWindow 组件已经渲染并注册了事件监听器
      setTimeout(() => {
        // 发送代码解释事件，包含选中代码和完整代码
        eventBus.emit('explainSnippet', { 
          selection: selectedCode, 
          fullCode, 
          language 
        });
      }, 300); // 300ms的延迟应足够组件渲染并注册事件监听器
      
      // 隐藏浮动按钮
      setAiBubbleVisible(false);
    }
  };
  
  return (
    <div className="editor-container">
      <div className="editor-toolbar">
        <div className="toolbar-controls">
          <div className="font-size-container">
            <FontIcon className="toolbar-icon-label" />
            <select value={font} onChange={handleFontChange} className="font-size-select">
              <option value="12px">12px</option>
              <option value="14px">14px</option>
              <option value="16px">16px</option>
              <option value="18px">18px</option>
              <option value="20px">20px</option>
              <option value="22px">22px</option>
              <option value="24px">24px</option>
              <option value="28px">28px</option>
              <option value="32px">32px</option>
            </select>
          </div>

          <div className="language-menu-container" ref={languageMenuRef}>
            <button onClick={toggleLanguageMenu} className="toolbar-button" title="选择编程语言">
              <CodeIcon /> {language === '' ? '选择一种语言...' : language === 'cpp' ? 'C++' : 'Python'} <span className="dropdown-arrow">▼</span>
            </button>
            {showLanguageMenu && (
              <div className="language-dropdown-menu">
                <div className="language-dropdown-item search-item">
                  <span className="search-icon">🔍</span> 选择一种语言...
                </div>
                <div className="language-dropdown-item" onClick={() => handleLanguageSelect('cpp')}>
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg" className="language-icon" alt="C++" />
                  C++
                </div>
                <div className="language-dropdown-item" onClick={() => handleLanguageSelect('python')}>
                  <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" className="language-icon" alt="Python" />
                  Python
                </div>
              </div>
            )}
          </div>

          <button onClick={() => fileInputRef.current?.click()} className="toolbar-button" title="上传文件">
            <UploadIcon /> 上传
          </button>
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} style={{ display: 'none' }} accept=".cpp,.c,.hpp,.h,.py,.txt" />

          <button onClick={handleDownload} className="toolbar-button" title="下载文件">
            <DownloadIcon /> 下载
          </button>

          <div className="file-menu-container" ref={fileMenuRef}>
            <button onClick={toggleFileMenu} className="toolbar-button" title="文件菜单">
              <FolderIcon /> 文件 <span className="dropdown-arrow">▼</span>
            </button>
            {showFileMenu && (
              <div className="file-menu">
                <button onClick={handleCreateNewFile}><PlusIcon /> 新建文件</button>
                <button onClick={handleOpenFileClick}><FolderOpenIcon /> 打开文件</button>
              </div>
            )}
          </div>

          <button onClick={handleSaveFile} className="toolbar-button" title="保存文件">
            <SaveIcon /> 保存
          </button>

          <input 
            type="text" 
            value={fileName} 
            onChange={handleFileNameChange} 
            className="file-name-input" 
            placeholder="文件名"
          />
        </div>
      </div>
      <div className="monaco-editor-container">
        <Editor
          height="calc(100% - 44px)" // 调整以匹配工具栏的精确高度 (8+28+8 = 44)
          language={language === 'cpp' ? 'cpp' : 'python'}
          value={code}
          onChange={handleEditorChange}
          options={{
            fontSize: parseInt(font),
            minimap: { enabled: true },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            readOnly: isCompiling || isRunning, // 在编译或运行时禁止编辑
            contextmenu: true // 启用右键菜单
          }}
          theme="vs" // 浅色主题
          onMount={handleEditorDidMount}
        />
      </div>
      
      {/* 代码列表弹窗 */}
      {showCodeList && (
        <CodeListModal
          onClose={() => setShowCodeList(false)}
          onSelectFile={handleSelectCodeFile}
        />
      )}
      {/* 添加 AI 气泡按钮 */}
      {aiBubbleVisible && (
      <AiBubble
        position={aiBubblePosition}
        visible={aiBubbleVisible}
        onClick={handleAiBubbleClick}
        onClose={() => setAiBubbleVisible(false)}
      />
    )}
    </div>
  );
};

export default EditorWithBus;
